import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Membership = () => {
  const { t, language } = useLanguage();

  const seo = {
    fr: {
      title: "Membership Henry & Gaby – Accès au club privé & cercle de décideurs",
      description: "Rejoignez Henry & Gaby, un club privé sélectif réunissant dirigeants, investisseurs et personnalités autour de l'aviation, de l'hospitalité de prestige et des grandes rencontres internationales.",
      h1: "Membership Henry & Gaby"
    },
    en: {
      title: "Henry & Gaby Membership – Private Club Access & Circle of Decision-Makers",
      description: "Join Henry & Gaby, a selective private club bringing together executives, investors and personalities around aviation, prestigious hospitality and major international meetings.",
      h1: "Henry & Gaby Membership"
    }
  };

  const currentSeo = seo[language] || seo.fr;

  const cards = [
    {
      name: t.membership.business,
      price: t.membership.businessPrice,
      features: [
        t.membership.businessFeature1,
        t.membership.businessFeature2,
        t.membership.businessFeature3,
        t.membership.businessFeature4
      ],
      highlighted: false
    },
    {
      name: t.membership.magellan,
      price: t.membership.magellanPrice,
      features: [
        t.membership.magellanFeature1,
        t.membership.magellanFeature2,
        t.membership.magellanFeature3,
        t.membership.magellanFeature4
      ],
      highlighted: true
    },
    {
      name: t.membership.orlamonde,
      price: t.membership.orlamondePrice,
      features: [
        t.membership.orlamondeFeature1,
        t.membership.orlamondeFeature2,
        t.membership.orlamondeFeature3,
        t.membership.orlamondeFeature4
      ],
      highlighted: false
    }
  ];

  return (
    <div>
      <Helmet>
        <title>{currentSeo.title}</title>
        <meta name="description" content={currentSeo.description} />
        <link rel="canonical" href="https://henry-and-gaby.com/membership" />
        <meta property="og:title" content={currentSeo.title} />
        <meta property="og:description" content={currentSeo.description} />
        <meta property="og:url" content="https://henry-and-gaby.com/membership" />
      </Helmet>

      {/* Hero Section avec Logo */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '48px',
            alignItems: 'center',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {/* Texte à gauche */}
            <div style={{ minWidth: 0 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                border: '1px solid var(--border-light)',
                background: 'var(--bg-primary)',
                marginBottom: '24px'
              }}>
                <Crown size={16} color="var(--text-primary)" />
                <span className="body-small">Club Privé Henry & Gaby</span>
              </div>
              
              <h1 className="hero-large" style={{ marginBottom: '24px', lineHeight: 1.2 }}>
                {currentSeo.h1}
              </h1>
              
              <p className="body-large" style={{ 
                color: 'var(--text-secondary)',
                lineHeight: 1.8,
                marginBottom: '0'
              }}>
                {t.membership.heroDescription || "Bienvenue au Club Privé Henry & Gaby, un lieu d'exception réservé à une élite discernée, où le luxe et l'exclusivité se rencontrent pour offrir une expérience inégalée à ses membres. Découvrez un univers où discrétion, confidentialité et prestations haut de gamme sont au cœur de nos services."}
              </p>
            </div>
            
            {/* Logo à droite - centré sur mobile, à droite sur desktop */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_d055d881-e1a5-4e1b-9815-362b880d9c3a/artifacts/qek3j5bw_LOGO%20CLUB%20H%26G%20TR.png"
                alt="Logo Club Henry & Gaby"
                style={{
                  maxWidth: '280px',
                  width: '100%',
                  height: 'auto'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Membership Cards */}
      <section className="section-padding">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {cards.map((card, index) => (
              <div
                key={index}
                className="hover-lift"
                style={{
                  padding: '40px',
                  background: card.highlighted ? 'var(--bg-primary)' : 'var(--bg-secondary)',
                  border: card.highlighted ? '2px solid var(--text-primary)' : '1px solid var(--border-light)',
                  position: 'relative'
                }}
              >
                {card.highlighted && (
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    padding: '4px 8px',
                    background: 'var(--text-primary)',
                    color: 'var(--bg-primary)',
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.5px'
                  }}>
                    POPULAR
                  </div>
                )}
                <h3 className="heading-2" style={{ marginBottom: '8px' }}>
                  {card.name}
                </h3>
                <div className="hero-medium" style={{ marginBottom: '32px' }}>
                  {card.price}
                </div>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 32px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  {card.features.map((feature, idx) => (
                    <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <Check size={20} color="var(--text-primary)" style={{ marginTop: '2px', flexShrink: 0 }} />
                      <span className="body-regular">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="btn-primary"
                  style={{
                    width: '100%',
                    background: card.highlighted ? 'var(--text-primary)' : 'transparent',
                    color: card.highlighted ? 'var(--bg-primary)' : 'var(--text-primary)'
                  }}
                >
                  {t.membership.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <p className="hero-medium" style={{
            fontStyle: 'italic',
            color: 'var(--text-secondary)',
            maxWidth: '700px',
            margin: '0 auto 32px'
          }}>
            "{t.membership.quote}"
          </p>
          <p className="body-large" style={{
            color: 'var(--text-primary)',
            maxWidth: '900px',
            margin: '0 auto',
            lineHeight: 1.8
          }}>
            {t.membership.additionalInfo}
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section-padding">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '24px'
          }}>
            <img
              src="https://customer-assets.emergentagent.com/job_global-advisory-1/artifacts/eiouf7l6_IMG_adhesion.jpg"
              alt="Luxury yacht"
              style={{
                width: '100%',
                height: '300px',
                objectFit: 'cover'
              }}
            />
            <img
              src="https://customer-assets.emergentagent.com/job_global-advisory-1/artifacts/8ulc0ead_IMG_20240522_235232814.jpg"
              alt="Monaco harbor"
              style={{
                width: '100%',
                height: '300px',
                objectFit: 'cover'
              }}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Membership;